/**
 * livekitService — fetches LiveKit join tokens from the Go API.
 *
 * The Go API validates the user's session, checks permissions,
 * then proxies the request to the Vox service and returns the LiveKit JWT.
 */

const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:8080/api';

export interface LiveKitTokenResult {
  token: string;
  livekitUrl: string;
  roomName: string;
}

export interface VoiceParticipantResult {
  id: string;
  username: string;
  avatar_url: string | null;
  muted: boolean;
}

function trackSource(track: any): string {
  return String(track?.source || track?.track?.source || track?.publication?.source || track?.kind || '').toLowerCase();
}

function isMicrophoneTrack(track: any): boolean {
  const source = trackSource(track);
  return source.includes('microphone') || source === 'audio';
}

function isTrackMuted(track: any): boolean {
  const value = track?.muted ?? track?.track?.muted ?? track?.publication?.muted;
  if (typeof value === 'boolean') return value;
  const enabled = track?.enabled ?? track?.track?.enabled;
  if (typeof enabled === 'boolean') return !enabled;
  return false;
}

function participantMuted(participant: any): boolean {
  if (typeof participant?.isMicrophoneEnabled === 'boolean') {
    return !participant.isMicrophoneEnabled;
  }
  if (typeof participant?.microphoneEnabled === 'boolean') {
    return !participant.microphoneEnabled;
  }
  if (!Array.isArray(participant?.tracks)) {
    return false;
  }

  const microphoneTrack = participant.tracks.find(isMicrophoneTrack);
  return microphoneTrack ? isTrackMuted(microphoneTrack) : false;
}

/**
 * Request a token to join a server voice channel.
 */
export async function getVoiceChannelToken(channelId: string): Promise<LiveKitTokenResult> {
  return request(`/voice/channels/${channelId}/token`, 'POST', {});
}

export async function getVoiceChannelParticipants(channelId: string): Promise<VoiceParticipantResult[]> {
  const data = await requestJson(`/voice/channels/${channelId}/participants`, 'GET');
  if (!Array.isArray(data)) return [];
  return data
    .map((participant: any) => ({
      id: String(participant.identity || participant.sid || ''),
      username: String(participant.name || participant.identity || 'Utilisateur'),
      avatar_url: typeof participant.metadata === 'string' && participant.metadata.trim() !== ''
        ? participant.metadata
        : null,
      muted: participantMuted(participant),
    }))
    .filter((participant) => participant.id);
}

/**
 * Request a token for a DM audio call.
 * Set canPublishVideo / canShareScreen to true to upgrade to a video call.
 */
export async function getDMCallToken(
  userId: string,
  opts: { canPublishVideo?: boolean; canShareScreen?: boolean } = {}
): Promise<LiveKitTokenResult> {
  return request(`/voice/dm/${userId}/token`, 'POST', {
    can_publish_video: opts.canPublishVideo ?? false,
    can_share_screen: opts.canShareScreen ?? false,
  });
}

// ---------------------------------------------------------------------------

async function request(path: string, method: string, body: unknown): Promise<LiveKitTokenResult> {
  const data = await requestJson(path, method, body);
  return {
    token: data.token,
    livekitUrl: data.livekit_url,
    roomName: data.room_name,
  };
}

async function requestJson(path: string, method: string, body?: unknown): Promise<any> {
  const token = localStorage.getItem('access_token');

  const resp = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: method === 'GET' ? undefined : JSON.stringify(body ?? {}),
  });

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: 'unknown error' }));
    throw new Error(err.error ?? `HTTP ${resp.status}`);
  }

  return resp.json();
}
